import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { pickTranslation } from '@/i18n/pickTranslation';
import { BADGE_ICONS, BADGE_STYLES, commercialBadge, discountPercent, excursionHighlights } from '@/lib/excursionCard';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=600&q=80';
const fmt = (n, lang) => Number(n).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', { maximumFractionDigits: 2 });

// Day / party the traveller gave (see lib/excursionPrefill.js) follow the link to the offer page.
const withQuery = (href, query) => (query ? `${href}${href.includes('?') ? '&' : '?'}${query}` : href);

/** Marketplace card of an excursion (other categories keep their own card). */
export function ExcursionCard({ service, query = '' }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').slice(0, 2);
  const exc = service.details?.excursion || {};
  const images = (service.images || []).filter(Boolean);
  const discount = discountPercent(service.price, exc.reference_price);
  const badge = commercialBadge(service);
  const highlights = excursionHighlights(exc);
  const title = pickTranslation(service, 'title', i18n.language) || service.title;

  return (
    <Link to={withQuery(service._href || `/services/${service.service_id}`, query)} data-testid={`service-card-${service.service_id}`} className="block min-w-0">
      <div className="card-service group cursor-pointer h-full flex flex-col">
        <div className="relative h-48 sm:h-52 overflow-hidden">
          <img src={images[0] || FALLBACK_IMG} alt={title} width="640" height="416" loading="lazy"
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
          {discount > 0 ? (
            <span className="absolute top-3 left-3 rounded-full bg-rose-500 text-white text-xs font-bold px-2.5 py-1 shadow-sm"
              data-testid={`excursion-discount-${service.service_id}`}>
              🔥 {t('excursionCard.discount', { pct: discount })}
            </span>
          ) : (
            <span className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-primary">
              {t('services.badgeExcursion')}
            </span>
          )}
          {badge && (
            <span className={`absolute top-3 right-3 rounded-full ${BADGE_STYLES[badge]} text-xs font-semibold px-2.5 py-1 shadow-sm`}
              data-testid={`excursion-badge-${service.service_id}`}>
              {BADGE_ICONS[badge]} {t(`excursionCard.badges.${badge}`)}
            </span>
          )}
          {images.length > 1 && (
            <span className="absolute bottom-3 right-3 rounded-full bg-black/55 text-white text-xs font-medium px-2 py-0.5"
              data-testid={`excursion-photos-${service.service_id}`}>
              📷 1/{images.length}
            </span>
          )}
        </div>
        <div className="p-4 sm:p-5 flex flex-col flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-primary leading-snug line-clamp-2 min-h-[2.75rem] sm:min-h-[3.25rem] break-words">{title}</h3>
          {highlights.length > 0 && (
            // Computer: the three facts on one line when the card is wide enough
            // (« Journée complète · Plongée sous-marine · Repas inclus »): slightly
            // smaller text and tighter separators, full labels, icons kept.
            // Narrower cards and phones: the facts wrap as before.
            <p className="mt-1.5 text-xs lg:text-[11px] lg:tracking-tight text-ink-light flex flex-wrap gap-x-1.5 lg:gap-x-1 gap-y-1"
              data-testid={`excursion-highlights-${service.service_id}`}>
              {highlights.map((h, i) => (
                <span key={h.key} className="whitespace-nowrap">
                  {i > 0 && <span className="text-slate-300 mr-1.5 lg:mr-1">·</span>}
                  <span className="text-[0.9em] mr-0.5">{h.icon}</span>{t(h.labelKey, h.params)}
                </span>
              ))}
            </p>
          )}
          <p className="text-sm text-ink-light mt-2 mb-3 line-clamp-2 min-h-[2.5rem] break-words">
            {pickTranslation(service, 'description', i18n.language) || service.description}
          </p>
          {service.location && (
            <div className="flex items-center text-sm text-ink-light mb-3 min-w-0">
              <MapPin className="w-4 h-4 mr-1 text-accent shrink-0" />
              <span className="truncate">{service.location}</span>
            </div>
          )}
          <div className="flex items-end justify-between gap-3 mt-auto pt-3 border-t border-slate-100">
            <div className="min-w-0">
              {discount > 0 && (
                <p className="text-xs text-ink-muted line-through" data-testid={`excursion-reference-${service.service_id}`}>
                  {fmt(exc.reference_price, lang)} THB
                </p>
              )}
              <p className="leading-tight">
                <span className="text-lg sm:text-xl font-bold text-primary">{fmt(service.price, lang)} THB</span>
                <span className="text-xs text-ink-light"> / {t('excursionCard.perPerson')}</span>
              </p>
            </div>
            <Button size="sm" className="btn-primary text-sm py-2 shrink-0 max-sm:min-h-11" data-testid={`book-button-${service.service_id}`}>
              {t('services.bookButton')}
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
}
